import React, { useState, useEffect, useMemo } from "react";

import { getUser } from "auth/authenticate";
import { BookingContext } from "./BookingContex";
import { fetchAuthenticatedPatientProfile } from "../services/profileService";

// ⬇️ NEW
import BookingBridge from "../services/BookingBridge";

export function BookingProvider({
  children,
  selectedAptData,
  doctorDetails,
  userDetails,
  userTimeZone,
  onAuthenticationSuccess,
}) {
  const {
    localDateDisplay,
    localTimeDisplay,
    slotData: { timeUtc } = {},
  } = selectedAptData;
  const { doctorId, doctorName: name, displayingFee } = doctorDetails;
  const { userId, username } = userDetails;

  const user = getUser();
  const isLoggedIn = user?.userType !== "GUEST" && user?.userId !== "GUEST";

  // --- Identity model ----------------------------------------------------
  const initialStatus = isLoggedIn ? "logged_in" : "guest";

  const [bookingPerson, setBookingPerson] = useState({
    id: userId || "",
    username: username || "",
    name: { firstName: "", lastName: "" },
    registered: isLoggedIn || false, // has an account
    authenticated: isLoggedIn || false, // currently logged in
    verified: isLoggedIn || false, // otp or session verified
    bookingForSelf: true,
    authStatus: initialStatus, // "guest" | "registered_needs_verification" | "logged_in"
  });

  // --- Step navigation ----------------------------------------------------
  const [step, setStep] = useState(isLoggedIn ? 2 : 1);
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => Math.max(1, prev - 1));

  // --- Contact details ----------------------------------------------------
  const [contactDetails, setContactDetails] = useState({
    enteredEmail: "",
    enteredPhone: "",
    verifiedEmail: "",
    verifiedPhone: "",
  });

  // --- OTP state ----------------------------------------------------------
  const [otpState, setOtpState] = useState({
    otpVerifying: false,
    otpVerified: false,
    otpCodeSent: false,
    otpEntered: false,
  });

  // --- Patient (dependents) ----------------------------------------------
  const [patient, setPatient] = useState({
    name: { firstName: "", lastName: "" },
    birthDate: "",
    gender: "",
    address: "",
    isNewDependent: true,
    patientId: "",
    parentId: "",
  });

  // --- Derived flags for convenience -------------------------------------
  const isGuest = bookingPerson.authStatus === "guest";
  const isRegistered =
    bookingPerson.authStatus === "registered_needs_verification";
  const isAuthenticated = bookingPerson.authStatus === "logged_in";

  useEffect(() => {
    // console.log("BookingProvider -> bookingPerson updated:", bookingPerson);
  }, [bookingPerson]);

  // --- External authentication handling
  useEffect(() => {
    if (isLoggedIn) {
      markAsAuthenticated();
      if (typeof onAuthenticationSuccess === "function") {
        onAuthenticationSuccess(user);
      }
    } else {
      markAsLoggedOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (isAuthenticated && bookingPerson.authStatus === "logged_in") {
      (async () => {
        try {
          const profile = await fetchAuthenticatedPatientProfile();
          setContactDetails({
            enteredEmail: profile.email,
            enteredPhone: profile.phone,
            verifiedEmail: profile.email,
            verifiedPhone: profile.phone,
          });
          setBookingPerson((prev) => ({
            ...prev,
            email: profile.email,
            phone: profile.phone,
            name: {
              ...prev.name,
              firstName: profile.firstName || prev.name.firstName,
              lastName: profile.lastName || prev.name.lastName,
            },
          }));
        } catch (e) {
          console.error("Profile fetch failed in provider", e);
        }
      })();
    }
  }, [isAuthenticated]);

  // --- State transition helpers ------------------------------------------

  const updateContactAfterVerification = (email, phone) => {
    setContactDetails((prev) => ({
      ...prev,
      enteredEmail: email,
      enteredPhone: phone,
      verifiedEmail: email,
      verifiedPhone: phone,
    }));
    setBookingPerson((prev) => ({
      ...prev,
      verified: true,
    }));
  };

  const markAsRegistered = () => {
    setBookingPerson((prev) => ({
      ...prev,
      registered: true,
      authenticated: false,
      verified: false,
      authStatus: "registered_needs_verification",
    }));
    setOtpState({
      otpVerifying: false,
      otpVerified: false,
      otpCodeSent: false,
      otpEntered: false,
    });
  };

  const markAsAuthenticated = () => {
    setBookingPerson((prev) => ({
      ...prev,
      registered: true,
      authenticated: true,
      verified: true,
      authStatus: "logged_in",
    }));
    setStep(2); // jump to Step 2 automatically
  };

  const markAsGuest = () => {
    setBookingPerson((prev) => ({
      ...prev,
      registered: false,
      authenticated: false,
      verified: false,
      authStatus: "guest",
    }));
    setOtpState({
      otpVerifying: false,
      otpVerified: false,
      otpCodeSent: false,
      otpEntered: false,
    });
  };

  const markAsVerifiedGuest = () => {
    setBookingPerson((prev) => ({
      ...prev,
      verified: true,
      authStatus: "guest",
    }));
  };

  const markAsLoggedOut = () => {
    setBookingPerson((prev) => ({
      ...prev,
      authenticated: false,
      verified: false,
      authStatus: prev.registered ? "registered_needs_verification" : "guest",
    }));
    setStep(1);
  };

  const updateName = (firstName, lastName) => {
    setBookingPerson((prev) => ({
      ...prev,
      name: { firstName, lastName },
    }));
  };

  // ============== BRIDGE WIRING (NEW, NON-BREAKING) =================
  // 1) expose callbacks once
  useEffect(() => {
    BookingBridge.setActions({
      prevStep,
      nextStep,
      markAsAuthenticated,
      markAsLoggedOut,
      setBookingPerson,
      setPatient,
      setContactDetails,
      // if you want: markAsRegistered, markAsGuest, updateContactAfterVerification
      markAsRegistered,
      markAsGuest,
      updateContactAfterVerification,
    });
    // no cleanup required; actions are stable references in this component lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) mirror the latest state into the bridge
  useEffect(() => {
    BookingBridge.setSnapshot({
      step,
      authStatus: bookingPerson.authStatus,
      isAuthenticated,
      isGuest,
      isRegistered,

      // core models
      bookingPerson,
      contactDetails,
      patient,

      // appointment selection
      selectedAptData: {
        localDateDisplay,
        localTimeDisplay,
        slotData: { timeUtc },
      },

      // doctor
      doctorDetails: {
        doctorId,
        doctorName: name,
        displayingFee,
      },

      // misc
      userDetails,
      userTimeZone,
    });
  }, [
    step,
    bookingPerson,
    contactDetails,
    patient,
    localDateDisplay,
    localTimeDisplay,
    timeUtc,
    doctorId,
    name,
    displayingFee,
    userDetails,
    userTimeZone,
    isAuthenticated,
    isGuest,
    isRegistered,
  ]);
  // ================================================================

  // --- Context value ------------------------------------------------------
  const value = useMemo(
    () => ({
      step,
      setStep,
      nextStep,
      prevStep,

      bookingPerson,
      setBookingPerson,
      isGuest,
      isRegistered,
      isAuthenticated,

      contactDetails,
      setContactDetails,

      otpState,
      setOtpState,

      updateContactAfterVerification,
      markAsRegistered,
      markAsAuthenticated,
      markAsGuest,
      markAsVerifiedGuest,
      markAsLoggedOut,
      updateName,

      patient,
      setPatient,

      selectedAptData,
      doctorDetails,
      userDetails,
      userTimeZone,
      onAuthenticationSuccess,
    }),
    [
      step,
      bookingPerson,
      contactDetails,
      otpState,
      patient,
      selectedAptData,
      doctorDetails,
      userDetails,
      onAuthenticationSuccess,
      userTimeZone,
      isGuest,
      isRegistered,
      isAuthenticated,
    ]
  );

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
}
